import { CalendarClock, CheckCircle2, CircleDollarSign, Wallet } from "lucide-react";
import { money } from "@/lib/rides";
import { useWallet } from "@/components/CreditWallet";
import { Avatar } from "@/components/Profiles";
import { useAuth } from "@/context/AuthContext";
import { useMyProfile, useMyRides, upcomingRides } from "@/lib/rider";
import { RideCard } from "@/components/rider/RideCard";
import { SectionTitle } from "@/components/rider/Nav";

export function Greeting({ onProfile }) {
  const { user } = useAuth();
  const { data: p } = useMyProfile();
  return (
    <button type="button" onClick={onProfile} className="flex w-full items-center gap-3 text-left" data-testid="rider-greeting">
      <Avatar photo={p?.photo} name={user?.name} size="h-12 w-12" testid="rider-avatar" />
      <span className="min-w-0">
        <span className="block text-xs text-muted-foreground">Welcome back</span>
        <span className="block truncate font-display text-xl font-bold tracking-tight" data-testid="rider-name">{user?.name}</span>
      </span>
      <span className="ml-auto rounded-full border border-white/15 px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="rider-profile-link">Profile</span>
    </button>
  );
}

function Card({ icon: Icon, label, value, onClick, testid, tone = "text-foreground" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-card p-3 text-left transition-[border-color,transform] hover:-translate-y-0.5 hover:border-primary/50">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0">
        <span className="block truncate text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        <span className={`block font-display text-lg font-bold leading-tight ${tone}`} data-testid={`${testid}-value`}>{value}</span>
      </span>
    </button>
  );
}

export function SummaryCards({ go }) {
  const { data: w } = useWallet();
  const { data: p } = useMyProfile();
  const { data: rides } = useMyRides();
  const owed = p?.amount_owed ?? 0;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="summary-cards">
      <Card icon={Wallet} label="Available Credit" value={money(w?.available ?? 0)} onClick={() => go("credit")} testid="summary-credit" />
      <Card icon={CircleDollarSign} label="Amount Owed" value={money(owed)} tone={owed > 0 ? "text-amber-300" : "text-foreground"} onClick={() => go("credit", "owed")} testid="summary-owed" />
      <Card icon={CheckCircle2} label="Rides Completed" value={p?.rides_as_rider ?? 0} onClick={() => go("rides", "completed")} testid="summary-completed" />
      <Card icon={CalendarClock} label="Upcoming Rides" value={upcomingRides(rides).length} onClick={() => go("rides", "upcoming")} testid="summary-upcoming" />
    </div>
  );
}

export function UpcomingPreview({ go, onFollow }) {
  const { data: rides } = useMyRides();
  const list = upcomingRides(rides).slice(0, 3);
  return (
    <section className="space-y-3" data-testid="upcoming-preview">
      <SectionTitle action={<button type="button" className="text-xs font-semibold text-primary" onClick={() => go("rides", "upcoming")} data-testid="upcoming-see-all">See all</button>}>
        Upcoming rides
      </SectionTitle>
      {!list.length && <p className="rounded-2xl border border-dashed border-white/15 p-4 text-sm text-muted-foreground" data-testid="upcoming-empty">No upcoming rides yet. Book one above.</p>}
      {list.map((r) => <RideCard key={r.id} ride={r} onFollow={onFollow} />)}
    </section>
  );
}
